import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetStorageLocations, dataSetStorageUnits } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { ConfirmationDialog, ProjectDialog } from '@zz2/zz2-ui';
import { CustomTable } from '../../../components/table/CustomTableWrapper';
import { IApiError } from '../../../@types/other';
import { useLocation } from 'react-router-dom';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import { IStorageLocation } from '../../../@types/model/masterData/storageLocation/storageLocation';
import { IStorageLocationFormValues } from '../../../@types/model/masterData/storageLocation/storageLocationFormValues';
import { setStorageLocationMasterDataIndexedDB, setStorageUnitMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import StorageLocationHttpService from '../../../services/http/masterData/storageLocationHttpService';
import StorageLocationModelHelper from '../../../@types/model/masterData/storageLocation/storageLocationModelHelper';
import StorageLocationForm from './form/StorageLocationEditForm';
import StorageUnitHttpService from '../../../services/http/masterData/storageUnitHttpService';

const StorageLocationScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const storageLocations = useAppSelector(x => x.masterData.storageLocations);
    const storageUnits = useAppSelector(x => x.masterData.storageUnits);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedStorageLocation, setSelectedStorageLocation] = useState<IStorageLocation | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingStorageLocation, setDeletingStorageLocation] = useState<IStorageLocation | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    
    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await StorageLocationHttpService.getList();
                const res2 = await StorageUnitHttpService.getStorageUnitData();
                dispatch(dataSetStorageLocations(res.data));
                dispatch(dataSetStorageUnits(res2.data));
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading storage locations.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const refreshData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            try {
                setIsLoading(true);
                const [storageUnitRes, storageLocationRes] = await Promise.all([
                    StorageUnitHttpService.getStorageUnitData(),
                    StorageLocationHttpService.getList(),
                ]);
                dispatch(dataSetStorageUnits(storageUnitRes.data));
                dispatch(dataSetStorageLocations(storageLocationRes.data));

                await setStorageUnitMasterDataIndexedDB(storageUnitRes.data);
                await setStorageLocationMasterDataIndexedDB(storageLocationRes.data);
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred loading storage locations.');
            } finally {
                setIsLoading(false);
            }
        } else {
            fetchData();
        }
    }

    const onSubmit = async (values : IStorageLocationFormValues) => {
        setIsLoading(true);

        try {
            const upsert = StorageLocationModelHelper.createUpsert(values, selectedStorageLocation);
            const res = await StorageLocationHttpService.upsert(upsert);
            const updatedList = upsertArrayElement(storageLocations, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetStorageLocations(updatedList));
            await setStorageLocationMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedStorageLocation ? 'Storage location updated successfully.' : 'Storage location added successfully.');
            onModalDismiss();
            refreshData();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating storage location data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeStorageLocation = async () => {
        if (!deletingStorageLocation) return;
        setIsLoading(true);
        try {
            const res = await StorageLocationHttpService.delete(deletingStorageLocation.id);
            const updatedList = upsertArrayElement(storageLocations, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetStorageLocations(updatedList));
            await setStorageLocationMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Storage location deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting storage location.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedStorageLocation(null);
    };

    const edit = (selectedRow : IStorageLocation) => {
        setSelectedStorageLocation(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IStorageLocation) : void => {
        setDeletingStorageLocation(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingStorageLocation(null);
    };

    const deleteButtonTooltip = (row : IStorageLocation) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Storage Location', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return StorageLocationModelHelper.createFormValues(
                selectedStorageLocation,
                storageUnits
            );
        }
    }, [
        selectedStorageLocation,
        isDialogOpen
    ]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IStorageLocation>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Brand', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Brand', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IStorageLocation) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={storageLocations}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedStorageLocation ? 'Edit Storage Location' : 'Add Storage Location'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <StorageLocationForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                        storageUnits={storageUnits}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Storage Location'}
                open={!!deletingStorageLocation}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeStorageLocation}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default StorageLocationScreen;
